import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { IDropdownOption } from '@ui-modules/types';
import { IFacility } from '@common/interfaces';
import { useGlobalContext, useRepository } from '@context';
import { Serializer } from '@common/utils';
import { SessionStorageKeys } from '@common/constants';

let savedAgency: string;

interface IFacilityResponse {
  agencyName: string;
  agenciesOptions: IDropdownOption[];
  facility: IFacility;
  isLoading: boolean;
}

const useFacility = (): IFacilityResponse => {
  const facilityId = sessionStorage.getItem(SessionStorageKeys.FACILITY)!;

  const { agencyName, facility, setAgencyName, setFacility } = useGlobalContext();
  const { facilityRepository } = useRepository();

  const [agenciesOptions, setAgenciesOptions] = useState<IDropdownOption[]>([]);

  const {
    data: facilityData,
    isLoading,
    refetch,
  } = useQuery('get-facility', () => facilityRepository.getFacility(agencyName, facilityId), {
    refetchOnWindowFocus: false,
    enabled: !!agencyName && (facility.city.length === 0 || facility.country.length === 0),
  });

  useEffect(() => {
    if (facilityData) {
      setFacility(Serializer.formatFacility(facilityData));
    }
  }, [facilityData, setFacility]);

  useEffect(() => {
    const agencies = sessionStorage.getItem(SessionStorageKeys.AGENCIES)!;

    if (agencies) {
      const options = agencies.split(',').map((a): IDropdownOption => ({ value: a, label: a }));
      setAgenciesOptions(options);
    }
  }, []);

  useEffect(() => {
    const agencies = sessionStorage.getItem(SessionStorageKeys.AGENCIES)!;
    const localAgencyName = sessionStorage.getItem(SessionStorageKeys.SELECTED_AGENCY);

    if (localAgencyName && agencies.split(',').includes(localAgencyName)) {
      setAgencyName(localAgencyName);
    } else if (!agencyName && agenciesOptions?.length) {
      setAgencyName(agenciesOptions[0].value);
      sessionStorage.setItem(SessionStorageKeys.SELECTED_AGENCY, agenciesOptions[0].value);
    }
  }, [agenciesOptions, agencyName, setAgencyName]);

  useEffect(() => {
    if (savedAgency && savedAgency !== agencyName) refetch();
    if (agencyName) savedAgency = agencyName;
  }, [agencyName, refetch]);

  return { agencyName, agenciesOptions, facility, isLoading };
};

export default useFacility;
